if (!customElements.get('swiper-slider')) {
  customElements.define(
    'swiper-slider',
    class SwiperSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.observer = null;
        this.mediaQuery = null;
        this.navigationContainer = null;
        this.onSwiperUpdate = this.onSwiperUpdate.bind(this);
        this.onSwiperResize = this.onSwiperResize.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);
      }

      connectedCallback() {
        if (this.swiper) return;

        this.swiperContainer = this.querySelector('.swiper');
        this.jsonScript = this.querySelector('script[data-swiper-config]');

        if (!this.swiperContainer || !this.jsonScript) {
          console.warn('SwiperSlider: missing container or config', this);
          return;
        }

        const { initOnMediaQuery } = this.getSwiperConfig() || {};
        if (initOnMediaQuery) {
          this.mediaQuery = window.matchMedia(initOnMediaQuery);
          this.mediaQuery.addEventListener('change', this.onMediaChange);
          this.syncMediaQuery();
          return;
        }

        this.initObserver();
      }

      onMediaChange() {
        this.syncMediaQuery();
      }

      syncMediaQuery() {
        if (this.mediaQuery?.matches) {
          this.initObserver();
          return;
        }

        this.observer?.disconnect();
        this.observer = null;
        this.destroySwiper();
      }

      initObserver() {
        if (!window.Swiper) {
          if (document.readyState === 'loading') {
            window.addEventListener('load', () => this.initObserver(), { once: true });
          }
          return;
        }

        if (!('IntersectionObserver' in window)) {
          this.initializeSwiper();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            this.observer.disconnect();
            this.observer = null;
            this.initializeSwiper();
          },
          { rootMargin: '600px' }
        );

        this.observer.observe(this);
      }

      getSwiperConfig() {
        try {
          const userConfig = JSON.parse(this.jsonScript.textContent);
          const defaults = {
            spaceBetween: 20,
            watchOverflow: true,
            a11y: {
              enabled: true,
              prevSlideMessage: 'Previous slide',
              nextSlideMessage: 'Next slide',
            },
          };

          return { ...defaults, ...userConfig };
        } catch (error) {
          console.error('SwiperSlider: invalid JSON config', error);
          return null;
        }
      }

      initializeSwiper() {
        if (this.swiper || !window.Swiper) return;

        const userConfig = this.getSwiperConfig();
        if (!userConfig) return;

        const { initOnMediaQuery, initialSlideOnMediaQuery, navigationContainer, ...config } = userConfig;

        if (initialSlideOnMediaQuery?.mediaQuery && window.matchMedia(initialSlideOnMediaQuery.mediaQuery).matches) {
          config.initialSlide = initialSlideOnMediaQuery.index;
        }

        this.navigationContainer = navigationContainer ? this.querySelector(navigationContainer) : null;

        this.swiper = new window.Swiper(this.swiperContainer, config);
        this.classList.add('is-initialized');
        this.dispatchEvent(new CustomEvent('swiper:initialized'));
        this.normalizeListSlideRoles();
        this.toggleNavigation();
        this.bindEvents();
      }

      normalizeListSlideRoles() {
        const listWrapper = this.swiperContainer?.querySelector('.swiper-wrapper[role="list"]');
        if (!listWrapper) return;

        listWrapper.querySelectorAll('.swiper-slide').forEach((slide) => {
          slide.setAttribute('role', 'listitem');
        });
      }

      toggleNavigation() {
        if (!this.swiper) return;

        const slidesCount = this.swiper.slides?.length ?? 0;
        const perView = this.swiper.params?.slidesPerView ?? 1;
        const shouldHide = perView !== 'auto' && slidesCount <= perView;

        if (this.navigationContainer) {
          this.navigationContainer.hidden = shouldHide;
        }

        [this.swiper.navigation?.nextEl, this.swiper.navigation?.prevEl].flat().forEach((element) => {
          if (!element) return;
          element.style.display = shouldHide ? 'none' : '';
        });
      }

      bindEvents() {
        this.swiper.on('resize', this.onSwiperResize);
        this.swiper.on('update', this.onSwiperUpdate);
        this.swiper.on('slideChange transitionEnd', () => this.normalizeListSlideRoles());
      }

      onSwiperResize() {
        this.toggleNavigation();
      }

      onSwiperUpdate() {
        this.normalizeListSlideRoles();
        this.toggleNavigation();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
        this.mediaQuery?.removeEventListener('change', this.onMediaChange);
        this.mediaQuery = null;
        this.navigationContainer = null;

        this.destroySwiper();
      }

      destroySwiper() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        this.classList.remove('is-initialized');
      }
    }
  );
}
